import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../context/notificationContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as ReportTypeAPI from "../../api/reportType";
import { Button } from "../../component/form/Button";
import { Field } from "../../types/FromComponent.types";
import { FormComponent } from "../../component/form/FormComponent";
import TableComponent from "../../component/form/TableComponent";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import "./ReportTypeEditScreen.css";

type ReportFieldItem = {
    id: number;
    label: string;
    field_type: string;
};

const FIELD_TYPE_OPTIONS = ["text"];

const toReportField = (item: Record<string, unknown>): ReportFieldItem => ({
    id: Number(item.id ?? item.field_id ?? 0),
    label: String(item.label ?? item.field_name ?? ""),
    field_type: String(item.field_type ?? item.type ?? ""),
});

const ReportTypeEditScreen: React.FC = () => {
    const { association } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();
    const params = useParams();

    const reportTypeId = Number(params.reportTypeId ?? 0);
    const associationId = String(association.id);

    const [reportTypeName, setReportTypeName] = useState("");
    const [reportFields, setReportFields] = useState<ReportFieldItem[]>([]);
    const [editingField, setEditingField] = useState<ReportFieldItem | null>(null);

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadReportTypeDetail = useCallback(async () => {
        const response = await ReportTypeAPI.detail(reportTypeId);
        if (!isSuccessCode(response.error_code)) {
            return handleApiError(response.request_id, response.error_code);
        }

        const detailSource = Array.isArray(response.data) ? response.data[0] : response.data;
        const detail = (detailSource ?? {}) as Record<string, unknown>;
        const fieldsSource = detail.fields ?? detail.report_fields ?? detail.report_type_fields ?? [];
        const mappedFields = (Array.isArray(fieldsSource) ? fieldsSource : [])
            .map((field: Record<string, unknown>) => toReportField(field))
            .filter((field) => field.id > 0);

        setReportTypeName(String(detail.name ?? detail.report_type_name ?? ""));
        setReportFields(mappedFields);
    }, [handleApiError, reportTypeId]);

    useEffect(() => {
        if (!reportTypeId) {
            addNotification("Invalid report type", "Missing report type id", "error");
            nav("/reports/types");
            return;
        }
        setLoadingStatus(true);
        loadReportTypeDetail().finally(() => setLoadingStatus(false));
    }, [addNotification, loadReportTypeDetail, nav, reportTypeId, setLoadingStatus]);

    const reportTypeFields: Field[] = [
        { name: "name", placeholder: "Report type name", type: "text", df: reportTypeName },
    ];

    const addFieldFields: Field[] = [
        { name: "name", placeholder: "Field name", type: "text" },
        {
            name: "field_type",
            placeholder: "Field type",
            type: "dropdown",
            options: FIELD_TYPE_OPTIONS.map((fieldType) => ({ id: fieldType, value: fieldType })),
        },
    ];

    const editFieldFields: Field[] = editingField ? [
        { name: "label", placeholder: "Field name", type: "text", df: editingField.label },
        {
            name: "field_type",
            placeholder: "Field type",
            type: "dropdown",
            df: editingField.field_type,
            options: FIELD_TYPE_OPTIONS.map((fieldType) => ({ id: fieldType, value: fieldType })),
        },
    ] : [];

    const handleUpdateReportType = async (data: { [key: string]: string }) => {
        if (!data.name?.trim()) {
            return addNotification("Validation error", "Report type name is required", "error");
        }

        setLoadingStatus(true);
        const response = await ReportTypeAPI.update({
            report_type_id: reportTypeId,
            name: data.name.trim(),
        }, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Updated", "Report type updated successfully", "success");
        await loadReportTypeDetail();
        setLoadingStatus(false);
    };

    const handleAddField = async (data: { [key: string]: string }) => {
        if (!data.name?.trim() || !data.field_type?.trim()) {
            return addNotification("Validation error", "Field name and type are required", "error");
        }

        setLoadingStatus(true);
        const response = await ReportTypeAPI.addField({
            report_type_id: reportTypeId,
            label: data.name.trim(),
            key: data.name.trim().toLowerCase().replace(/\s+/g, "_"),
            field_type: data.field_type.trim(),
        });
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Created", "Field added successfully", "success");
        await loadReportTypeDetail();
        setLoadingStatus(false);
    };

    const handleUpdateField = async (data: { [key: string]: string }) => {
        if (!editingField?.id) return;
        if (!data.name?.trim() || !data.field_type?.trim()) {
            return addNotification("Validation error", "Field name and type are required", "error");
        }

        setLoadingStatus(true);
        const response = await ReportTypeAPI.updateField({
            field_id: editingField.id,
            label: data.name.trim(),
            key: data.name.trim().toLowerCase().replace(/\s+/g, "_"),
            field_type: data.field_type.trim(),
        });
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Updated", "Field updated successfully", "success");
        setEditingField(null);
        await loadReportTypeDetail();
        setLoadingStatus(false);
    };

    const handleDeleteField = async (fieldId: number) => {
        setLoadingStatus(true);
        const response = await ReportTypeAPI.deleteField({ field_id: fieldId });
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }

        addNotification("Deleted", "Field deleted successfully", "success");
        if (editingField?.id === fieldId) {
            setEditingField(null);
        }
        await loadReportTypeDetail();
        setLoadingStatus(false);
    };

    const columns: Column[] = [
        { header: "ID", accessor: "id" },
        { header: "Name", accessor: "label" },
        { header: "Type", accessor: "field_type" },
    ];

    const tableRows = reportFields.map((field) => ({ ...field, raw: field }));

    const buttons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faPen} title="Edit" />,
            bc: "green",
            placeholder: "Edit",
            action: { param: "raw", function: (field: ReportFieldItem) => setEditingField(field) }
        },
        {
            button: <FontAwesomeIcon icon={faTrash} title="Delete" />,
            bc: "red",
            placeholder: "Delete",
            action: { param: "id", function: handleDeleteField }
        },
    ];

    if (loading) {
        return <LoadingScreenLocal />;
    }

    return (
        <div className="report-type-edit-screen">
            <div className="report-type-edit-header">
                <Button name="Back to list" action={() => nav("/reports/types")} color="red" />
            </div>
            <FormComponent
                title="Update report type"
                fields={reportTypeFields}
                onSubmit={handleUpdateReportType}
                button={{ name: "Save report type" }}
            />
            <FormComponent
                title="Add field"
                fields={addFieldFields}
                onSubmit={handleAddField}
                button={{ name: "Add field" }}
            />
            <TableComponent
                data={tableRows}
                columns={columns}
                buttons={buttons}
            />
            {editingField && (
                <FormComponent
                    title={`Edit field #${editingField.id}`}
                    fields={editFieldFields}
                    onSubmit={handleUpdateField}
                    button={{ name: "Save field" }}
                />
            )}
        </div>
    );
};

export default ReportTypeEditScreen;
